import { z } from "zod";
import {
  PLAYLIST_MAX_VIDEOS,
  PLAYLIST_TYPES,
  PLAYLIST_VISIBILITIES,
  VIEWER_ROLES,
} from "../constants/index";
import { nonEmpty, uuid } from "./_primitives";

export const playlistItemSchema = z.object({
  videoId: uuid,
  sortOrder: z.number().int().nonnegative(),
  // Accepts either a specific version UUID, the literal "pin-at-send"
  // (server resolves to the video's currentVersionId on save), or null
  // (follow current version at play time).
  pinnedVersionId: z.union([uuid, z.literal("pin-at-send")]).nullable().optional(),
});

export const createPlaylistSchema = z.object({
  name: nonEmpty.max(200),
  description: z.string().optional(),
  type: z.enum(PLAYLIST_TYPES).default("custom"),
  // CEO Rev 1.0: campaign-scoped templates. Optional + additive — set when a
  // playlist is created from inside a campaign workspace.
  campaignId: uuid.nullable().optional(),
  items: z
    .array(playlistItemSchema)
    .min(1, "Playlist must contain at least one video")
    .max(
      PLAYLIST_MAX_VIDEOS,
      `A playlist can contain at most ${PLAYLIST_MAX_VIDEOS} videos`,
    ),
  // Audit V1: visibility + role tags + public slug.
  visibility: z.enum(PLAYLIST_VISIBILITIES).default("private"),
  roles: z.array(z.enum(VIEWER_ROLES)).default([]),
  publicSlug: z
    .string()
    .min(3)
    .max(64)
    .regex(/^[a-z0-9-]+$/, "lowercase alphanumeric + hyphen")
    .nullable()
    .optional(),
});

export const updatePlaylistSchema = createPlaylistSchema.partial();

export type PlaylistItemInput = z.infer<typeof playlistItemSchema>;
export type CreatePlaylistInput = z.infer<typeof createPlaylistSchema>;
export type UpdatePlaylistInput = z.infer<typeof updatePlaylistSchema>;
